import React, { useCallback, useEffect, useState, useRef } from "react";

import Loading from "../components/Loading";
import { useDispatch, useSelector } from "react-redux";

import { login } from "../actions/auth";
import { useNavigate } from "react-router-dom";

const returnUser = (navigate) => {
  try {
    const user = JSON.parse(localStorage.getItem("profile"))?.value[0].role;
    if (user) {
      navigate("/main");
    }
    return;
  } catch (error) {
    return error;
  }
};

export default function Login() {
  const [userData, setUserData] = useState({ username: "", password: "" });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const error = useSelector((state) => state.auth?.error);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setLoading(true);
      dispatch(login(userData, navigate));
    },
    [dispatch, navigate, userData],
  );

  const returnUserRef = useRef(returnUser);
  
  useEffect(() => {
    returnUserRef.current = returnUser(navigate);
  }, [navigate]);

  useEffect(() => {
    if (error && !showAlert) {
      setLoading(false);
      setShowAlert(true);
    }
  }, [error, showAlert]);

  const handleChange = (e) => {
    if (showAlert) {
      setShowAlert(false);
    }

    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 rounded-full bg-emerald-600/15 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-indigo-900/10 blur-3xl" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 mb-4">
            <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Authentication System</h1>
          <p className="text-slate-400 text-sm mt-1">Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-8 shadow-2xl space-y-5">
          {showAlert && (
            <div
              className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-red-100"
              role="alert"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-red-400/20 bg-red-500/15 text-red-300">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v4m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.67 18h16.66a1 1 0 00.88-1.47l-7.5-13a1 1 0 00-1.74 0z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold tracking-wide">Login failed</p>
                  <p className="text-sm text-red-100/80">
                    The username or password is incorrect. Check your credentials and try again.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2">Username</label>
              <input
                name="username"
                placeholder="Enter username"
                type="text"
                onChange={handleChange}
                autoComplete="username"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-indigo-500/50 transition-colors text-sm"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2">Password</label>
              <input
                name="password"
                placeholder="Enter password"
                type="password"
                onChange={handleChange}
                autoComplete="current-password"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-indigo-500/50 transition-colors text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-white/10 disabled:text-slate-500 text-white font-semibold py-3 rounded-xl transition-all text-sm mt-2"
            >
              {!loading ? "Sign In" : <Loading />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
